/**
 * 对标爆款选择页 - UI/UX Pro Max 优化版
 *
 * 设计系统：
 * - Soft UI Evolution 风格
 * - 清晰的视觉层次
 * - 无 emoji，使用 SVG 图标
 *
 * 核心流程：
 * 1. 选择类型（使用图标替代 emoji）
 * 2. 选择对标爆款
 * 3. 查看拆解可视化
 * 4. 开始生成
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ChevronRight,
  ChevronDown,
  Target,
  TrendingUp,
  Info,
  Check,
  Building2,
  Zap,
  Sword,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';

// 爽点类型标签 - 使用颜色类名
const COOL_POINT_TYPE_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  'reversal': { label: '绝地翻盘', color: 'bg-red-500', bgColor: 'bg-red-50' },
  'revenge': { label: '复仇', color: 'bg-orange-500', bgColor: 'bg-orange-50' },
  'face-slap': { label: '打脸', color: 'bg-amber-500', bgColor: 'bg-amber-50' },
  'reveal': { label: '揭秘', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
  'power-up': { label: '升级', color: 'bg-green-500', bgColor: 'bg-green-50' },
  'conquest': { label: '征服', color: 'bg-purple-500', bgColor: 'bg-purple-50' },
};

// 内置爆款模板
const TEMPLATES = [
  {
    id: 'urban-rebirth-1',
    title: '重生之商界大佬',
    genre: 'urban',
    logline: '重生回到2010年，用前世记忆打造商业帝国',
    tags: ['重生', '商战', '打脸'],
    coolPoints: [
      { type: 'reveal', position: 5 },
      { type: 'face-slap', position: 12 },
      { type: 'power-up', position: 20 },
      { type: 'revenge', position: 35 },
      { type: 'face-slap', position: 45 },
      { type: 'reveal', position: 55 },
      { type: 'conquest', position: 65 },
      { type: 'reversal', position: 80 },
      { type: 'conquest', position: 92 },
    ],
    scores: { overall: 8.5, structure: 8.8, rhythm: 8.2, sensation: 8.6 },
  },
  {
    id: 'xuanhuan-upgrade-1',
    title: '万古神帝',
    genre: 'xuanhuan',
    logline: '废柴少年觉醒逆天天赋，踏上神帝之路',
    tags: ['废柴逆袭', '升级', '玄幻'],
    coolPoints: [
      { type: 'reversal', position: 3 },
      { type: 'face-slap', position: 10 },
      { type: 'power-up', position: 18 },
      { type: 'revenge', position: 28 },
      { type: 'reveal', position: 38 },
      { type: 'face-slap', position: 48 },
      { type: 'power-up', position: 58 },
      { type: 'conquest', position: 70 },
      { type: 'reversal', position: 85 },
      { type: 'conquest', position: 95 },
    ],
    scores: { overall: 8.8, structure: 9.0, rhythm: 8.5, sensation: 9.0 },
  },
  {
    id: 'xianxia-1',
    title: '修仙从炼丹开始',
    genre: 'xianxia',
    logline: '废柴少年觉醒丹道天赋，以丹入道',
    tags: ['修仙', '炼丹', '废柴逆袭'],
    coolPoints: [
      { type: 'reveal', position: 5 },
      { type: 'face-slap', position: 15 },
      { type: 'power-up', position: 25 },
      { type: 'revenge', position: 40 },
      { type: 'reveal', position: 55 },
      { type: 'conquest', position: 70 },
      { type: 'reversal', position: 88 },
    ],
    scores: { overall: 8.2, structure: 8.5, rhythm: 8.0, sensation: 8.1 },
  },
  {
    id: 'urban-system-1',
    title: '超级学霸系统',
    genre: 'urban',
    logline: '学渣获得系统，逆袭成为全能学霸',
    tags: ['系统', '学霸', '都市'],
    coolPoints: [
      { type: 'reveal', position: 2 },
      { type: 'face-slap', position: 8 },
      { type: 'power-up', position: 15 },
      { type: 'face-slap', position: 22 },
      { type: 'revenge', position: 32 },
      { type: 'reveal', position: 45 },
      { type: 'conquest', position: 60 },
      { type: 'face-slap', position: 75 },
      { type: 'conquest', position: 90 },
    ],
    scores: { overall: 8.0, structure: 8.2, rhythm: 7.8, sensation: 8.0 },
  },
];

// 类型配置 - 使用图标替代 emoji
const GENRES = [
  { value: 'urban', label: '都市', icon: Building2 },
  { value: 'xuanhuan', label: '玄幻', icon: Zap },
  { value: 'xianxia', label: '仙侠', icon: Sword },
];

export default function Reference() {
  const navigate = useNavigate();
  const { setGenre, setAnalyzedStructure } = useAppStore();

  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<typeof TEMPLATES[0] | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const filteredTemplates = selectedGenre
    ? TEMPLATES.filter(t => t.genre === selectedGenre)
    : TEMPLATES;

  const handleSelectTemplate = (template: typeof TEMPLATES[0]) => {
    setSelectedTemplate(template);
    setShowAnalysis(true);
  };

  const handleStartGenerate = () => {
    if (!selectedTemplate) return;

    // 设置类型
    setGenre(selectedTemplate.genre as any);

    // 将模板结构存入 store（作为参考结构）
    setAnalyzedStructure({
      threeAct: {
        act1: { chapters: 25, wordCount: 125000 },
        act2: { chapters: 50, wordCount: 250000 },
        act3: { chapters: 25, wordCount: 125000 },
      },
      herosJourney: [],
      beats: [],
      emotionalArc: [],
    } as any, selectedTemplate.id);

    navigate('/generate');
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        {/* 标题 */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">选择对标爆款</h1>
          <p className="text-slate-500">
            选择一本爆款作为参考，AI 会分析其结构和爽点设计
          </p>
        </div>

        {/* 类型筛选 - 使用图标 */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button
            onClick={() => setSelectedGenre(null)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
              selectedGenre === null
                ? 'bg-primary-500 text-white shadow-md'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            全部
          </button>
          {GENRES.map((g) => {
            const Icon = g.icon;
            return (
              <button
                key={g.value}
                onClick={() => setSelectedGenre(g.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                  selectedGenre === g.value
                    ? 'bg-primary-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {g.label}
              </button>
            );
          })}
        </div>

        {/* 爆款列表 */}
        <div className="grid md:grid-cols-2 gap-4">
          {filteredTemplates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl border-2 p-5 cursor-pointer transition-all duration-200 ${
                selectedTemplate?.id === template.id
                  ? 'border-primary-500 ring-2 ring-primary-500/20 shadow-md'
                  : 'border-slate-200 hover:border-primary-300 hover:shadow-md'
              }`}
              onClick={() => handleSelectTemplate(template)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-slate-900">{template.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{template.logline}</p>
                </div>
                {selectedTemplate?.id === template.id && (
                  <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center shrink-0 ml-3">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              {/* 标签 */}
              <div className="flex flex-wrap gap-2 mb-4">
                {template.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* 评分 */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="font-semibold text-slate-900">
                    {template.scores.overall}
                  </span>
                </div>
                <div className="text-xs text-slate-400">
                  结构 {template.scores.structure} · 节奏 {template.scores.rhythm}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 拆解可视化 */}
        <AnimatePresence>
          {showAnalysis && selectedTemplate && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-2xl p-6 border border-primary-100"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary-600" />
                  {selectedTemplate.title} - 结构拆解
                </h3>
                <button
                  onClick={() => setShowAnalysis(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              </div>

              {/* 爽点分布可视化 */}
              <div className="mb-6">
                <div className="text-sm text-slate-600 mb-2 font-medium">爽点分布曲线</div>
                <div className="relative h-24 bg-white rounded-lg border border-slate-200 overflow-hidden">
                  {/* 三幕结构背景 */}
                  <div className="absolute inset-0 flex">
                    <div className="w-1/4 bg-blue-50/50 border-r border-slate-200" />
                    <div className="w-1/2 bg-green-50/50 border-r border-slate-200" />
                    <div className="w-1/4 bg-amber-50/50" />
                  </div>

                  {/* 位置刻度 */}
                  <div className="absolute top-2 left-0 right-0 flex justify-between px-3 text-xs text-slate-400 font-medium">
                    <span>开篇</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>结局</span>
                  </div>

                  {/* 爽点标记 */}
                  <div className="absolute bottom-3 left-0 right-0 h-14">
                    {selectedTemplate.coolPoints.map((point, index) => {
                      const typeInfo = COOL_POINT_TYPE_LABELS[point.type];
                      return (
                        <div
                          key={index}
                          className="absolute bottom-0 transform -translate-x-1/2 group"
                          style={{ left: `${point.position}%` }}
                        >
                          <div
                            className={`w-3 h-3 rounded-full ${typeInfo.color} ring-2 ring-white shadow-sm`}
                            title={typeInfo.label}
                          />
                          {index % 2 === 0 && (
                            <div className="text-xs text-slate-500 mt-1 whitespace-nowrap">
                              {typeInfo.label}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 爽点类型统计 */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
                {Object.entries(COOL_POINT_TYPE_LABELS).map(([type, info]) => {
                  const count = selectedTemplate.coolPoints.filter(
                    (p) => p.type === type
                  ).length;
                  return (
                    <div
                      key={type}
                      className={`rounded-lg p-3 text-center ${info.bgColor}`}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${info.color} mx-auto mb-1`}
                      />
                      <div className="text-xs text-slate-600">{info.label}</div>
                      <div className="font-semibold text-slate-900">{count}个</div>
                    </div>
                  );
                })}
              </div>

              {/* 评分详情 */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: '综合', value: selectedTemplate.scores.overall, color: 'text-primary-600' },
                  { label: '结构', value: selectedTemplate.scores.structure, color: 'text-blue-600' },
                  { label: '节奏', value: selectedTemplate.scores.rhythm, color: 'text-green-600' },
                  { label: '爽感', value: selectedTemplate.scores.sensation, color: 'text-amber-600' },
                ].map((item) => (
                  <div key={item.label} className="text-center bg-white rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">{item.label}</div>
                    <div className={`text-xl font-bold ${item.color}`}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* 提示 */}
              <div className="bg-white/80 rounded-lg p-4 flex items-start gap-3 mb-4 border border-slate-200">
                <Info className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium mb-1 text-slate-900">对标说明</p>
                  <p>
                    AI 将参考这本爆款的爽点分布和节奏设计，生成结构相似但内容原创的大纲。
                    你可以在下一步调整参数。
                  </p>
                </div>
              </div>

              {/* 开始生成按钮 */}
              <button
                onClick={handleStartGenerate}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-semibold py-4 rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-all duration-200 cursor-pointer"
              >
                <TrendingUp className="w-5 h-5" />
                基于这本爆款生成大纲
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
